import { Component } from '@angular/core';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-geojson-point-weather-station defines a class to display weather stations.  The data format is geojson.
 *
 * Example of the data file:
 * --------------------------------
 * {"type": "FeatureCollection",
 *	"crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
 *	"product":"NWS Warnings at [2018 03/23 03:00:01 UTC]",
 *	"created_by":"Ventra County Watershed Protection District and TriLynx Systems",
 *	"created_from":"https://forecast.weather.gov/wwamap/wwatxtget.php",
 *	"features": [
 *	{"type": "Feature", "properties": { "Class":"SVR", "Name":"Severe Weather Statement",
		"StationName": "Weather Station 1234", "Temperature":"82", "DewPoint":"42", "WindDirection":"NE",
		"WindSpeed":"12", "WindGust":"15", "Description":
		"Severe Weather Statement\nNational Weather Service Wichita KS\n521 PM CDT TUE  ...  \n\nMCGUIRE\n"
 *	}, "geometry": { "type": "Point", "coordinates": [-105.575071,39.4213]}},
 *	  ...
 *	]
 * }
 * --------------------------------
 *
 * Within the configuration file, weather stations can be included:
 * "overlayLayers": [
 * 		{"id": "weatherStationLayer", "label": "Weather Stations", "url": "map/overlay-layers/weatherStations.geojson",
 *		 "class": "data-layer-geojson-point-weather-station", "refresh": false },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-point-weather-station',
	standalone: false,
	template: `
		<div class="checkboxDiv"><label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css', 'data-layer-templates.css']
})
export class DataLayerGeojsonPointWeatherStationComponent extends DataLayerGeojsonPointComponent {


	constructor(
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService ) {
		super(mapService, dataLayerService, configService, userMessageService);
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// first check whether the information has been read.
	// display the markers on the map and set the layer to active
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established

			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		this.active = true;
	}

	drawMarkers () {
		console.log('Drawing ' + this.value.features.length + ' stations.');
		this.mapLayer = L.geoJson(this.value, {
			// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
			pointToLayer: this.placeMarkers,
			onEachFeature: this.onEachPointFeature
		}).addTo(this.mapService.map);
	}

	// extended classes should implement this function
	onEachPointFeature(feature, layer) {
		const stationName = feature.properties.StationName;
		const temperature = feature.properties.Temperature;
		const dewpoint = feature.properties.DewPoint;
		const windspeed = feature.properties.WindSpeed;
		const windgust = feature.properties.WindGust;
		const winddirection = feature.properties.WindDirection;
		let popupContent = '<pre>' + feature.properties.Description + '</pre>';
		const options = { maxHeight: 500, minWidth: 500 };
		const tooltipContent = stationName + '<br>' +
			temperature + ' F Temperature<br>' +
			dewpoint + ' F Dewpoint<br>' +
			winddirection + ' Wind Direction<br>' +
			windspeed + ' mph Wind Speed<br>' +
			windgust + ' mph Wind Gust<br>Click for detailed information';

		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent);
	}

	// extended classes should implement this function
	placeMarkers(feature, latlng) {
		const stationName = feature.properties.StationName;
		const temperature = feature.properties.Temperature;
		const dewpoint = feature.properties.DewPoint;
		const windspeed = feature.properties.WindSpeed;
		const windgust = feature.properties.WindGust;
		const winddirection = feature.properties.WindDirection;
		const marker = L.ExtraMarkers.icon({
				icon: 'fa-number',
				markerColor: 'yellow',			// marker background color
				shape: 'square',
				prefix: 'fa',
				innerHTML: '<svg width="90" height="60"> ' +
				'<style> .weatherNumbers {font-family: arial; font-size: 16px; font-weight:100 }</style> ' +
				'<rect x="3" y="1" width="20" height="14" rx="3" ry="3" stroke="black" stroke-width="4" fill="black"/><text class="weatherNumbers" x="3" y="14" stroke="white"><tspan>' +
				temperature + '</tspan></text>' +
				'<rect x="33" y="1" width="20" height="14" rx="3" ry="3" stroke="black" stroke-width="4" fill="black"/><text class="weatherNumbers" x="33" y="14" stroke="white"><tspan>' +
				dewpoint + '</tspan></text>' +
				'<rect x="3" y="18" width="20" height="14" rx="3" ry="3" stroke="black" stroke-width="4" fill="black" /><text class="weatherNumbers" x="3" y="30" stroke="white"><tspan>' +
				winddirection + '</tspan></text>' +
				'<rect x="33"  y="18" width="20" height="14" rx="3" ry="3" stroke="black" stroke-width="4" fill="black"/><text class="weatherNumbers" x="33" y="30" stroke="white"><tspan>' +
				windspeed + '</tspan></text>' +
				'<rect x="17"  y="33" width="20" height="14" rx="3" ry="3" stroke="black" stroke-width="4" fill="black"/><text class="weatherNumbers" x="17" y="46" stroke="white"><tspan>' +
				windgust + '</tspan></text>' +
				'</svg>'
			});
		marker.options.shadowSize = [50, 75];
		// marker.options.title = "hello";
		return L.marker(latlng, {icon: marker});
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}

	}

	// from data-layer-service.parseCameraStations
	parseDataFile () {
		console.log('In data-layer-geojson-point-weather.parseDataFile.');
		const self = this;

		const requestStations = new XMLHttpRequest();
		requestStations.onload = function ( ) {
			if ( requestStations.readyState === requestStations.DONE && requestStations.status === 200 ) {

				// first, get json file content
				const allStations = JSON.parse(requestStations.responseText);
				// console.log(allStations);


				self.value = allStations;
				self.valueObservable.next(allStations);
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
					filename + ' for all ' + self.id + ' stations was successfully completed.');
				// console.log ( dataLayer );

			} else {
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' stations was UNSUCCESSFUL.');
			}
		};
		const filename = this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		requestStations.open('get', filename, true );
		requestStations.overrideMimeType('text/plain');
		console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
		requestStations.send();
	}

}
